'use client';

import { useState, useMemo, useEffect, useCallback, useRef, useSyncExternalStore } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, X, Grid, Maximize2, Camera, ZoomIn, ZoomOut, Link2, Check, TriangleAlert } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn, getPlaceholderImages, pluralize } from '@/lib/utils';
import {
  INITIAL_FILMSTRIP_EDGES,
  filmstripEdges,
  filmstripScrollBehavior,
  filmstripScrollTarget,
  photoParamIndex,
  withPhotoParam,
  withoutPhotoParam,
} from '@/lib/gallery';
import { shareOrCopy, type ShareOutcome } from '@/lib/share';
import { photoPreviewSrc, photoSrc } from '@/lib/photos';
import photoLoader from '@/lib/imageLoader';
import { listingViewTransitionName } from '@/lib/listingViewTransition';
import type { Photo } from '@/types/listing';

interface ListingGalleryProps {
  photos: Photo[];
  address: string;
  listingId?: string;
  /** Public MLS identity shared with the source card transition boundary. */
  mlsListingId?: string;
  photoCount?: number;
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const subscribeReducedMotion = (onChange: () => void) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const media = window.matchMedia(REDUCED_MOTION_QUERY);
  media.addEventListener?.('change', onChange);
  return () => media.removeEventListener?.('change', onChange);
};
const reducedMotionSnapshot = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches;
const serverReducedMotionSnapshot = () => false;

export default function ListingGallery({
  photos,
  address,
  listingId,
  mlsListingId,
  photoCount = 5,
}: ListingGalleryProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    reducedMotionSnapshot,
    serverReducedMotionSnapshot
  );
  // Photos the browser couldn't load (see markBroken), keyed by url.
  const [broken, setBroken] = useState<Set<string>>(new Set());
  // Mosaic tiles that have painted; until then the tile shimmers instead of
  // sitting as a flat grey rectangle.
  const [tilesLoaded, setTilesLoaded] = useState<Set<number>>(new Set());
  const markTileLoaded = useCallback((index: number) => {
    setTilesLoaded((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  }, []);
  // Click-to-zoom on the active lightbox photo; origin follows the cursor to pan.
  const [zoomed, setZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  // The filmstrip and the active thumb, so the strip can follow the photo.
  // `stripNode` is STATE, not a ref, so its arrival is something the centring
  // effect can depend on. Radix mounts `Dialog.Content` into a portal one commit
  // *after* `open` flips, so on the render where the lightbox opens the strip
  // does not exist yet — a ref would still be null, the effect would bail, and
  // nothing would ever change to make it run again. Verified on the deployed
  // site: a `?photo=86` deep link left `scrollLeft: 0` with the edge state still
  // at its pre-measurement value (no fades at all), i.e. `syncStripEdges` had
  // never once run. (W13 R129)
  const [stripNode, setStripNode] = useState<HTMLDivElement | null>(null);
  const activeThumbRef = useRef<HTMLButtonElement | null>(null);
  // Whether there is more strip to the left/right (so the edge fades only
  // appear when they mean something), and whether it fits at all — a
  // `justify-center` flex container whose content overflows pushes that content
  // off BOTH ends and makes the leading items unreachable, which is exactly the
  // bug W12 R118 fixed in the empty state. So the strip centres only when it
  // genuinely fits, and is left-aligned the moment it doesn't.
  const [stripEdges, setStripEdges] = useState(INITIAL_FILMSTRIP_EDGES);
  // The control that opened the lightbox, so focus returns to it on close. The
  // openers are gallery tiles / the "View all" button — NOT a Dialog.Trigger —
  // so Radix can't auto-restore, and a keyboard user closing the lightbox would
  // otherwise be dumped on <body> and lose their place (WCAG 2.4.3).
  const lightboxOpenerRef = useRef<HTMLElement | null>(null);

  // Use placeholder images if no real photos
  const displayPhotos = useMemo(() => {
    if (photos.length > 0) return photos;
    if (listingId) {
      return getPlaceholderImages(listingId, Math.max(photoCount, 5));
    }
    return [];
  }, [photos, listingId, photoCount]);

  // The MLSListings feed pads the photo array beyond what the CDN actually holds
  // (e.g. 42 entries for 20 real images); the extras 404. `photoCount` is an
  // unreliable bound — valid photos sometimes run past it and 404s sometimes
  // fall inside it — so we prune by what the browser can truly load: any image
  // that errors is dropped so the gallery never shows a broken frame and the
  // "N photos" counts stay honest. Never fabricated, only filtered.
  const markBroken = useCallback((photo: Photo) => {
    setBroken((prev) => {
      if (prev.has(photo.url)) return prev;
      const next = new Set(prev);
      next.add(photo.url);
      return next;
    });
  }, []);

  const photosToShow = useMemo(() => {
    const live = displayPhotos.filter((p) => !broken.has(p.url));
    // Never blank the gallery — if everything somehow failed, keep the originals.
    return live.length > 0 ? live : displayPhotos;
  }, [displayPhotos, broken]);

  // Guard every render (and every step) against a just-pruned index: `active` is
  // the stored index clamped into the current live range, so a stale-high index
  // left behind when a broken photo is dropped never reads past the array.
  const active = Math.min(activeIndex, photosToShow.length - 1);
  const photoTransitionName =
    !reducedMotion ? listingViewTransitionName(mlsListingId, 'photo') : undefined;
  // Smallest real frame of the active photo, for the lightbox to paint while the
  // full one arrives. `undefined` when the smallest thing available *is* the
  // image being loaded, so we never fetch the same bytes twice.
  const activePreview = photoPreviewSrc(photosToShow[active], 'hero');

  const goToNext = useCallback(() => {
    setIsImageLoading(true);
    setZoomed(false); // a new photo always opens fit-to-screen
    setActiveIndex((prev) => (Math.min(prev, photosToShow.length - 1) + 1) % photosToShow.length);
  }, [photosToShow.length]);

  const goToPrev = useCallback(() => {
    setIsImageLoading(true);
    setZoomed(false);
    setActiveIndex(
      (prev) => (Math.min(prev, photosToShow.length - 1) - 1 + photosToShow.length) % photosToShow.length
    );
  }, [photosToShow.length]);

  // Touch swipe navigation for the lightbox (parity with the iOS gallery).
  const touchStartX = useRef<number | null>(null);
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  }, []);
  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null || photosToShow.length < 2) return;
      // While zoomed, a swipe shouldn't yank to the next photo — let the tap toggle it back.
      if (zoomed) {
        touchStartX.current = null;
        return;
      }
      const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
      // ~40px threshold rejects taps and accidental drags.
      if (Math.abs(dx) > 40) (dx < 0 ? goToNext : goToPrev)();
      touchStartX.current = null;
    },
    [goToNext, goToPrev, photosToShow.length, zoomed]
  );

  // Neighboring frames to warm so arrow/swipe navigation paints instantly.
  const neighborIndexes = useMemo(() => {
    const n = photosToShow.length;
    if (n < 2) return [] as number[];
    const prev = (active - 1 + n) % n;
    const next = (active + 1) % n;
    return Array.from(new Set([prev, next])).filter((i) => i !== active);
  }, [active, photosToShow.length]);

  /**
   * The gallery lives in the URL as `?photo=N`.
   *
   * Two dead-ends it fixes, both confirmed on the deployed site:
   * - **Back left the listing.** With the lightbox open on the 86-photo Gilroy
   *   home, Back went straight to `/homes?cities=palo-alto&view=map`. On a phone
   *   Back — or the edge-swipe gesture — *is* how people dismiss a full-screen
   *   overlay, so a buyer closing the photos lost the home they were reading.
   * - **A photo could not be shared.** "Look at the kitchen" was unlinkable;
   *   Share copied the same URL whatever was on screen.
   *
   * Opening pushes one entry (so Back closes, Forward reopens); stepping through
   * photos **replaces** it — 86 entries would bury the buyer's real history, the
   * same reason a map pan under "search as I move" replaces (W12 R120).
   */
  const ownsHistoryEntryRef = useRef(false);

  const openLightbox = useCallback((index: number, opener?: HTMLElement | null) => {
    if (opener) lightboxOpenerRef.current = opener;
    setZoomed(false);
    setActiveIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    if (ownsHistoryEntryRef.current) {
      ownsHistoryEntryRef.current = false;
      // Pop our own entry so the URL, the history stack and the dialog agree —
      // the popstate handler below does the closing.
      window.history.back();
      return;
    }
    // Arrived by deep link, so there is no entry of ours to pop and back() would
    // leave the site. Drop the param instead, so re-sharing this URL after
    // closing doesn't reopen the gallery for the next person.
    window.history.replaceState(window.history.state, '', withoutPhotoParam(window.location.href));
    setLightboxOpen(false);
  }, []);

  // Open on arrival when the link names a photo. This has to be an effect: the
  // server render must not contain an open dialog (the URL is not part of the
  // cached HTML and it would be a hydration mismatch), so the earliest honest
  // moment to read `window.location` is after mount.
  /* eslint-disable react-hooks/set-state-in-effect -- see above: post-hydration
     URL read is the only correct moment, and it runs once. */
  useEffect(() => {
    const index = photoParamIndex(window.location.search, displayPhotos.length);
    if (index !== null) openLightbox(index);
    // Mount only: later changes come from clicks and popstate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Keep the URL pointed at what's on screen.
  useEffect(() => {
    if (!lightboxOpen) return;
    const next = withPhotoParam(window.location.href, active);
    if (next === window.location.href) return;
    if (ownsHistoryEntryRef.current || photoParamIndex(window.location.search, 1) !== null) {
      window.history.replaceState(window.history.state, '', next);
    } else {
      window.history.pushState(window.history.state, '', next);
      ownsHistoryEntryRef.current = true;
    }
  }, [lightboxOpen, active]);

  // Back/Forward drive the gallery.
  useEffect(() => {
    const onPopState = () => {
      // Whatever entry we pushed is no longer the current one.
      ownsHistoryEntryRef.current = false;
      const index = photoParamIndex(window.location.search, displayPhotos.length);
      if (index === null) {
        setLightboxOpen(false);
        return;
      }
      setZoomed(false);
      setActiveIndex(index);
      setLightboxOpen(true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [displayPhotos.length]);

  // Share the photo on screen. The URL already points at it, so this is purely
  // "give the buyer a way to reach that URL from inside the overlay".
  const [shareState, setShareState] = useState<ShareOutcome | null>(null);
  const shareTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
    },
    []
  );
  const sharePhoto = useCallback(async () => {
    const result = await shareOrCopy({
      title: address,
      text: `Photo ${active + 1} of ${photosToShow.length} — ${address}`,
      url: withPhotoParam(window.location.href, active),
    });
    if (result === 'dismissed' || result === 'shared') return;
    setShareState(result);
    if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
    shareTimerRef.current = setTimeout(() => setShareState(null), result === 'failed' ? 4000 : 1800);
  }, [address, active, photosToShow.length]);

  // Hide the floating "talk to a human" widget while the full-screen gallery is
  // open — it lives in a separate stacking context so a bare z-index can't cover
  // it. Reuses the same body-flag mechanism the mobile sticky CTA already uses.
  useEffect(() => {
    if (!lightboxOpen) return;
    document.body.dataset.lightboxOpen = '1';
    return () => {
      delete document.body.dataset.lightboxOpen;
    };
  }, [lightboxOpen]);

  /**
   * Keep the filmstrip centred on the photo being viewed, and keep the edge
   * fades honest.
   *
   * Measured on the deployed 86-photo listing at 1280: the strip is
   * **6,216 px wide in a 1,280 px viewport — 21% of it reachable without
   * scrolling** — and `scrollLeft` never moved off 0. Pressing `End` put the
   * active thumb at `offsetLeft: 6136`, five thousand pixels off-screen, so the
   * strip sat on thumbs 1–17 **with no thumb ringed at all**: at "86 / 86" the
   * control looked broken, and there was no way back to the neighbours of the
   * photo on screen. `scrollbar-hide` meant there wasn't even a scrollbar to
   * drag. (W13 R122)
   *
   * `scrollTo` on the strip itself — not `scrollIntoView`, which would also
   * scroll the document behind the dialog.
   */
  const syncStripEdges = useCallback(() => {
    const strip = stripNode;
    if (!strip) return;
    setStripEdges(
      filmstripEdges({
        scrollLeft: strip.scrollLeft,
        clientWidth: strip.clientWidth,
        scrollWidth: strip.scrollWidth,
      })
    );
  }, [stripNode]);

  // Rotating a phone changes whether the strip fits, and the fades/centring have
  // to follow. (The strip's own `onScroll` covers everything else.)
  useEffect(() => {
    if (!lightboxOpen) return;
    window.addEventListener('resize', syncStripEdges);
    return () => window.removeEventListener('resize', syncStripEdges);
  }, [lightboxOpen, syncStripEdges]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const strip = stripNode;
    const thumb = activeThumbRef.current;
    if (!strip || !thumb) return;
    const target = filmstripScrollTarget({
      thumbOffsetLeft: thumb.offsetLeft,
      thumbWidth: thumb.offsetWidth,
      clientWidth: strip.clientWidth,
      scrollWidth: strip.scrollWidth,
    });
    strip.scrollTo({
      left: target,
      behavior: filmstripScrollBehavior(strip.scrollLeft, target, strip.clientWidth),
    });
    syncStripEdges();
    // `stripNode` is a dependency because the strip mounts a commit after the
    // dialog opens, and `stripEdges.fits` because it changes the strip's layout
    // (`justify-center` ⇄ `justify-start`) and therefore every offset this
    // effect just measured — without it, the first measurement of a long strip
    // is computed against a layout that is about to be replaced. (W13 R122b)
  }, [lightboxOpen, active, stripNode, stripEdges.fits, syncStripEdges]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          goToNext();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          goToPrev();
          break;
        case 'Escape':
          e.preventDefault();
          closeLightbox();
          break;
        case 'Home':
          e.preventDefault();
          setZoomed(false);
          setActiveIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setZoomed(false);
          setActiveIndex(photosToShow.length - 1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, goToNext, goToPrev, closeLightbox, photosToShow.length]);

  if (displayPhotos.length === 0) {
    return (
      <div className="aspect-[16/9] rounded-card bg-raised flex flex-col items-center justify-center">
        <Camera className="w-16 h-16 text-ink-faint mb-3" />
        <p className="text-ink-muted font-medium">Photos coming soon</p>
        <p className="text-ink-faint text-sm mt-1">Check back later for property images</p>
      </div>
    );
  }

  // One clickable image tile — shared by the mosaic and the thin layouts so
  // broken-photo pruning and lightbox wiring stay identical across both.
  const galleryTile = (
    index: number,
    opts: { span?: string; sizes?: string; label?: string; overlay?: React.ReactNode } = {}
  ) => {
    const photo = photosToShow[index];
    const isHero = index === 0;
    return (
      <button
        key={photo.url}
        onClick={(e) => openLightbox(index, e.currentTarget)}
        className={cn('relative group overflow-hidden bg-overlay', opts.span)}
        style={index === 0 && photoTransitionName ? { viewTransitionName: photoTransitionName } : undefined}
        data-view-transition-name={index === 0 ? photoTransitionName : undefined}
        data-transition-type={index === 0 && photoTransitionName ? 'listing-detail' : undefined}
        aria-label={
          opts.label ??
          `Open full-screen gallery — ${address}, photo ${index + 1} of ${photosToShow.length}`
        }
      >
        {/* Shimmer under the photo while it decodes (see PhotoCarousel). */}
        {!tilesLoaded.has(index) && (
          <span
            aria-hidden
            className="animate-shimmer absolute inset-0 bg-overlay bg-[linear-gradient(110deg,transparent_35%,rgb(32_29_21/7%)_50%,transparent_65%)] bg-[length:200%_100%]"
          />
        )}
        <Image
          src={photoSrc(photo, isHero ? 'hero' : 'card')}
          loader={photoLoader}
          alt={`${address} - Photo ${index + 1}`}
          fill
          className={cn(
            'object-cover motion-safe:transition-opacity motion-safe:transition-transform motion-safe:duration-[300ms] motion-safe:ease-[cubic-bezier(.16,1,.3,1)] motion-safe:group-hover:scale-[1.025]',
            // The hero is the detail-page LCP candidate — never start it
            // transparent (Chrome skips transparent elements for LCP).
            tilesLoaded.has(index) || isHero ? 'opacity-100' : 'opacity-0'
          )}
          sizes={opts.sizes ?? '25vw'}
          priority={isHero}
          // The hero tile is the listing-detail LCP. `priority` emits the
          // preload link but does NOT reflect fetchpriority onto the <img> in
          // this Next version (confirmed in the DOM + a failed LCP-discovery
          // check), so set it explicitly — matching PhotoCarousel — to bias the
          // browser toward the LCP image over the other mosaic tiles.
          fetchPriority={isHero ? 'high' : undefined}
          onLoad={() => markTileLoaded(index)}
          onError={() => {
            markTileLoaded(index);
            markBroken(photo);
          }}
        />
        <div aria-hidden className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
        {opts.overlay}
      </button>
    );
  };

  /**
   * Below `sm` the mosaic collapses to the hero alone. The four side tiles are
   * `hidden` there — at 390 px the 4-column grid squeezed them to ~20 px-wide
   * slivers of a landscape photo, and the hero itself was a 176 × 400 portrait
   * crop. Being `hidden` also means their lazy images are never fetched, so the
   * mobile detail page drops from 5 gallery requests to 1. The count badge below
   * keeps "there are more photos" visible, and the tile still opens the lightbox.
   */
  const mobileCountBadge = (
    <span
      aria-hidden
      className="glass-blur absolute bottom-2.5 right-2.5 flex items-center gap-1.5 rounded-md bg-ink/65 px-2.5 py-1.5 text-xs font-medium text-white sm:hidden"
    >
      <Camera className="w-3.5 h-3.5" />
      {pluralize(photosToShow.length, 'photo')}
    </span>
  );

  return (
    <>
      {/* Photo grid — count-adaptive so thin listings never show empty filler.
          5+: editorial mosaic (big hero + 4). 1–4: a clean grid that fills.
          Below `sm`, every layout collapses to a single full-width hero. */}
      {photosToShow.length >= 5 ? (
        <div className="grid grid-cols-1 sm:grid-cols-4 sm:grid-rows-2 gap-1.5 h-[280px] sm:h-[400px] lg:h-[520px] rounded-card overflow-hidden">
          {galleryTile(0, {
            span: 'sm:col-span-2 sm:row-span-2',
            sizes: '(max-width: 639px) 92vw, 49vw',
            label: `Open full-screen gallery — ${address}, photo 1 of ${photosToShow.length}`,
            overlay: mobileCountBadge,
          })}
          {photosToShow.slice(1, 5).map((_, i) => {
            const showAll = i === 3 && photosToShow.length > 5;
            return galleryTile(i + 1, {
              span: 'hidden sm:block',
              // WCAG 2.5.3 (Label in Name): this tile shows the visible text
              // "+N photos", so its accessible name must CONTAIN that exact
              // string (voice-control users say what they see). Build the label
              // from the SAME pluralize() call as the overlay so singular/plural
              // always agree ("+1 photo" / "+25 photos").
              label: showAll
                ? `+${pluralize(photosToShow.length - 5, 'photo')} — see all ${photosToShow.length} in the full gallery`
                : undefined,
              overlay: showAll ? (
                <div aria-hidden className="absolute inset-0 bg-ink/65 flex items-center justify-center">
                  <span className="text-white font-semibold flex items-center gap-2">
                    <Grid className="w-5 h-5" />
                    +{pluralize(photosToShow.length - 5, 'photo')}
                  </span>
                </div>
              ) : null,
            });
          })}
        </div>
      ) : (
        <div
          className={cn(
            'grid grid-cols-1 gap-1.5 h-[280px] sm:h-[400px] lg:h-[520px] rounded-card overflow-hidden',
            photosToShow.length === 2 && 'sm:grid-cols-2',
            photosToShow.length >= 3 && 'sm:grid-cols-2 sm:grid-rows-2'
          )}
        >
          {photosToShow.map((_, index) =>
            galleryTile(index, {
              // 3-up: hero takes the tall left column; 1/2/4-up fill evenly.
              // Below `sm` only the hero shows (see mobileCountBadge).
              span: cn(
                index > 0 && 'hidden sm:block',
                photosToShow.length === 3 && index === 0 && 'sm:row-span-2'
              ),
              sizes: photosToShow.length === 1 ? '(max-width: 639px) 92vw, 96vw' : '(max-width: 639px) 92vw, 49vw',
              overlay: index === 0 && photosToShow.length > 1 ? mobileCountBadge : undefined,
            })
          )}
        </div>
      )}

      {/* View all button */}
      <button
        onClick={(e) => openLightbox(0, e.currentTarget)}
        className="mt-3 flex items-center gap-2 text-sm text-ink-muted hover:text-emerald-300 transition-colors"
      >
        <Maximize2 className="w-4 h-4" />
        View all {pluralize(photosToShow.length, 'photo')}
      </button>

      {/* Lightbox */}
      {/* Every dismissal — Escape, the X, a click on the overlay — has to go
          through closeLightbox so the URL and the history stack stay in step
          with what's on screen. */}
      <Dialog.Root
        open={lightboxOpen}
        onOpenChange={(open) => (open ? setLightboxOpen(true) : closeLightbox())}
      >
        <Dialog.Portal>
          {/* z-[60] sits above the floating contact widget + mobile sticky CTA
              (both z-50/z-40) so nothing paints over the full-screen gallery. */}
          <Dialog.Overlay className="fixed inset-0 bg-black/95 z-[60]" />
          <Dialog.Content
            className="fixed inset-0 z-[60] flex items-center justify-center"
            aria-describedby={undefined}
            onCloseAutoFocus={(e) => {
              // Return focus to the tile/button that opened the gallery so a
              // keyboard user lands back where they were, not on <body>.
              const opener = lightboxOpenerRef.current;
              if (opener && document.body.contains(opener)) {
                e.preventDefault();
                opener.focus();
              }
            }}
          >
            <Dialog.Title className="sr-only">Photo gallery for {address}</Dialog.Title>
            {/* Share this photo. The URL already carries `?photo=N` (R123), so
                the only thing missing was somewhere to reach it from — the
                page's own Share button is behind the full-screen overlay. */}
            <button
              onClick={sharePhoto}
              className="absolute right-28 top-4 z-10 p-2 text-white hover:text-white/70"
              aria-label={`Copy a link to photo ${active + 1}`}
            >
              <span className="sr-only" role="status" aria-live="polite">
                {shareState === 'copied'
                  ? `Link to photo ${active + 1} copied to clipboard`
                  : shareState === 'failed'
                    ? "Couldn't copy the link — copy it from the address bar instead."
                    : ''}
              </span>
              {shareState === 'copied' ? (
                <Check className="h-7 w-7 text-emerald-400" />
              ) : shareState === 'failed' ? (
                <TriangleAlert className="h-7 w-7 text-amber-400" />
              ) : (
                <Link2 className="h-7 w-7" />
              )}
            </button>

            {/* Zoom toggle */}
            <button
              onClick={() => setZoomed((z) => !z)}
              className="absolute top-4 right-16 text-white hover:text-white/70 p-2 z-10"
              aria-label={zoomed ? 'Zoom out' : 'Zoom in'}
              aria-pressed={zoomed}
            >
              {zoomed ? <ZoomOut className="w-7 h-7" /> : <ZoomIn className="w-7 h-7" />}
            </button>

            {/* Close button */}
            <Dialog.Close
              className="absolute top-4 right-4 text-white hover:text-white/70 p-2 z-10"
              aria-label="Close gallery"
            >
              <X className="w-8 h-8" />
            </Dialog.Close>

            {/* Navigation */}
            {photosToShow.length > 1 && (
              <>
                <button
                  onClick={goToPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-white/70 p-2 bg-white/10 rounded-full z-10"
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-white/70 p-2 bg-white/10 rounded-full z-10"
                  aria-label="Next photo"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            {/* Image */}
            <div
              className={cn(
                'relative w-full h-full max-w-5xl max-h-[80vh] mx-auto overflow-hidden',
                zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
              )}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              onClick={() => setZoomed((z) => !z)}
              onMouseMove={(e) => {
                if (!zoomed) return;
                // Map the cursor onto the transform-origin so the zoom pans to it.
                const rect = e.currentTarget.getBoundingClientRect();
                setZoomOrigin({
                  x: ((e.clientX - rect.left) / rect.width) * 100,
                  y: ((e.clientY - rect.top) / rect.height) * 100,
                });
              }}
            >
              {/* A real, already-cached frame of the SAME photo, painted under
                  the full-size one while it loads. Opening the lightbox used to
                  show a spinner over a dim rectangle for as long as a 1024 px
                  frame took to arrive; the buyer has just clicked a tile whose
                  thumb (or the MLS `/1/` variant, ~7 KB) is sitting in the
                  browser cache, so this costs no new request in the common case
                  and the photo appears instantly, sharpening a beat later. */}
              {isImageLoading && activePreview && (
                <Image
                  src={activePreview}
                  loader={photoLoader}
                  alt=""
                  aria-hidden
                  fill
                  className="object-contain blur-[2px]"
                  sizes="(max-width: 1024px) 100vw, 1024px"
                />
              )}
              {isImageLoading && !activePreview && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full motion-safe:animate-spin" />
                </div>
              )}
              <Image
                src={photoSrc(photosToShow[active], 'hero')}
                loader={photoLoader}
                alt={`${address} - Photo ${active + 1} of ${photosToShow.length}`}
                fill
                className={cn(
                  'object-contain motion-safe:transition-[opacity,transform] motion-safe:duration-[200ms]',
                  isImageLoading ? 'opacity-50' : 'opacity-100'
                )}
                style={{
                  transform: zoomed ? 'scale(2.2)' : 'scale(1)',
                  transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                }}
                // The lightbox frame is capped at `max-w-5xl` (1024 px) and
                // `max-h-[80vh]`, so `100vw` made wide desktops fetch a 1920 px
                // candidate for a box that never exceeds 1024.
                sizes="(max-width: 1024px) 100vw, 1024px"
                onLoad={() => setIsImageLoading(false)}
                onError={() => {
                  // A phantom frame: drop it and let the view settle on a real one.
                  setIsImageLoading(false);
                  markBroken(photosToShow[active]);
                }}
                priority
              />
              {/* Warm neighboring frames so swipe/arrow nav paints instantly. */}
              {neighborIndexes.map((i) => (
                <Image
                  key={`preload-${i}`}
                  src={photoSrc(photosToShow[i], 'hero')}
                  loader={photoLoader}
                  alt=""
                  aria-hidden
                  fill
                  className="object-contain opacity-0 pointer-events-none"
                  sizes="(max-width: 1024px) 100vw, 1024px"
                  onError={() => markBroken(photosToShow[i])}
                />
              ))}
            </div>

            {/* Bottom chrome: filmstrip, then counter, then hint — one stacked
                column so they cannot overlap. Two independent `bottom-*` values
                did: measured on the deployed 86-photo listing at 1280, the
                counter pill occupied y 824–860 and the strip y 788–836, so the
                pill was drawn across the bottom of the thumbnails. (W13 R122) */}
            <div className="absolute inset-x-0 bottom-4 flex flex-col items-center gap-2">
              {photosToShow.length > 1 && (
                <div className="relative w-full">
                  <div
                    ref={setStripNode}
                    onScroll={syncStripEdges}
                    // A 6,216 px strip with a hidden scrollbar had no way to
                    // move on a mouse; a vertical wheel over it now scrolls it
                    // sideways.
                    onWheel={(e) => {
                      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
                      e.preventDefault();
                      // `currentTarget` rather than the state node: the element
                      // is right here, and scroll position is not state.
                      e.currentTarget.scrollLeft += e.deltaY;
                    }}
                    className={cn(
                      'flex gap-2 overflow-x-auto px-4 scrollbar-hide',
                      stripEdges.fits ? 'justify-center' : 'justify-start'
                    )}
                  >
                    {photosToShow.map((photo, index) => (
                      <button
                        key={photo.url}
                        ref={index === active ? activeThumbRef : undefined}
                        onClick={() => {
                          setZoomed(false);
                          setActiveIndex(index);
                        }}
                        aria-label={`Go to photo ${index + 1} of ${photosToShow.length}`}
                        aria-current={index === active ? 'true' : undefined}
                        className={cn(
                          'relative h-12 w-16 flex-shrink-0 overflow-hidden rounded motion-safe:transition-[opacity,box-shadow]',
                          index === active
                            ? 'ring-2 ring-white ring-offset-2 ring-offset-black'
                            : 'opacity-60 hover:opacity-100'
                        )}
                      >
                        <Image
                          src={photoSrc(photo, 'micro')}
                          loader={photoLoader}
                          alt=""
                          aria-hidden
                          fill
                          className="object-cover"
                          sizes="64px"
                          onError={() => markBroken(photo)}
                        />
                      </button>
                    ))}
                  </div>
                  {/* "There is more strip this way" — the only signal left once
                      the scrollbar is hidden. Rendered only when true. */}
                  {stripEdges.left && (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-black to-transparent"
                    />
                  )}
                  {stripEdges.right && (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-black to-transparent"
                    />
                  )}
                </div>
              )}
              <div className="rounded-full bg-white/10 px-4 py-2 text-sm font-medium tabular-nums text-white">
                {active + 1} / {photosToShow.length}
              </div>
              <p className="hidden text-xs text-white/50 sm:block">
                Arrow keys to navigate · click the photo to zoom
              </p>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
